import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  FormFieldComponent,
  InputDirective,
  InputErrorComponent,
  LabelComponent,
} from '@cf/shared/forms/form-field';
import { ButtonComponent, LinkComponent } from '@cf/shared/ui/button';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { AvatarComponent } from '@cf/shared/ui/logos';
import { TextComponent, TextTruncateComponent } from '@cf/shared/ui/typography';
import { AccountService, CfSchemaMeetingInfo } from '@cf/temp/services';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { BookingPagePaymentComponent } from '../../booking-payment';
import { BookingPaymentStateService } from '../../booking-payment/services/booking-payment-state.service';
import { prepareBookingQuestionsDto } from '../../const';
import { IBookingForm } from '../../interfaces';
import { BookingInformationComponent } from '../../shared/booking-information/booking-information.component';
import { BookingGuestsComponent } from '../booking-guests/booking-guests.component';
import { BookingQuestionsComponent } from '../booking-questions';

@Component({
  selector: 'cf-booking-personal',
  templateUrl: './booking-personal.component.html',
  styleUrls: ['./booking-personal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    LinkComponent,
    AvatarComponent,
    NgIf,
    TextComponent,
    TextTruncateComponent,
    BookingInformationComponent,
    NgxTolgeeModule,
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    InputErrorComponent,
    BookingGuestsComponent,
    BookingPagePaymentComponent,
    InputDirective,
    BookingQuestionsComponent,
    CfIconDirective,
  ],
})
export class BookingPersonalComponent implements OnInit {
  @ViewChild(BookingGuestsComponent) guestsComponent?: BookingGuestsComponent;
  @ViewChild(BookingPagePaymentComponent)
  paymentComponent!: BookingPagePaymentComponent;

  @Input({ required: true }) booking!: CfSchemaMeetingInfo;
  @Input({ required: true }) timezone!: string;
  @Input({ required: true }) time!: string;
  @Input({ required: true }) day!: string;
  @Input({ required: true }) duration!: string;
  @Input() isPreview!: boolean;
  @Input() previewMode: 'desktop' | 'mobile' = 'desktop';

  @Output() emitBack = new EventEmitter();
  @Output() emitSubmit = new EventEmitter();

  @Input({ required: true }) form!: FormGroup<IBookingForm>;

  constructor(
    public readonly accountService: AccountService,
    private readonly bookingPaymentStateService: BookingPaymentStateService,
  ) {}

  stripeAccountConnected = this.bookingPaymentStateService.stripeAccountConnected;

  ngOnInit() {
    const storedForm = localStorage.getItem('guest_booking_form');
    if (!this.accountService.account.id && storedForm && !this.isPreview) {
      this.form.patchValue(JSON.parse(storedForm));
    }
  }

  submit() {
    if (this.form.invalid || !!this.guestsComponent?.errorMsg()) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = {
      ...this.form.value,
      questions: prepareBookingQuestionsDto(this.form.controls.questions),
    };

    if (this.bookingPaymentStateService.stripeAccountConnected()) {
      this.paymentComponent.createToken().subscribe((res) => {
        this.setPersonalInfoToStorage();
        if (res.paymentMethod) {
          this.form.controls.payment_method_id.patchValue(res.paymentMethod.id);
          formValue.payment_method_id = res.paymentMethod.id;
          this.emitSubmit.emit(formValue);
        }
        if (res.error) {
          this.paymentComponent.errorMsg = res.error.message;
          this.paymentComponent.cd.detectChanges();
        }
      });
    } else {
      setTimeout(() => {
        this.emitSubmit.emit(formValue);
        this.setPersonalInfoToStorage();
      }, 200);
    }
  }

  setPersonalInfoToStorage() {
    const data = {
      name: this.form.value.name,
      email: this.form.value.email,
    };

    localStorage.setItem('guest_booking_form', JSON.stringify(data));
  }
}
